import "./CancellationRequests.scss";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { Modal, Box } from "@mui/material";
import { CancellationColumns } from "../../SourceData";
import api from "../../utils/api";
import BlueButton from "../../components/BlueButton/BlueButton";
import ColorButton from "../../components/ColorButton/ColorButton";
import RejectGif from "./../../assets/reject-gif.gif";
import CloseIcon from "./../../assets/close-icon.svg";
import SubscriptionBookingModal from "../../components/SubscriptionBookingModal/SubscriptionBookingModal";
import { setNotificationCount } from "../../redux/notificationsSlice";
import { usePermissions } from "./../../hooks/usePermissions";

const CancellationRequests = () => {
  const { hasPermission } = usePermissions();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(false);

  // Listen to notification count changes from Redux (updated by middleware)
  const notificationCount = useSelector(
    (state) => state.notifications?.counts?.cancellationRequests || 0
  );

  const fetchRequests = async () => {
    if (!hasPermission(106)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
        status: status,
      };

      const response = await api.get("/refunds", { params });

      if (response.data.status === "success") {
        const data = response.data.data;
        const totalCount = response.data.totalCount || 0;

        setRequests(data);
        setTotalCount(totalCount);

        const pendingCount = status === "pending" ? totalCount : 0;
        dispatch(
          setNotificationCount({
            key: "cancellationRequests",
            count: pendingCount,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching refund requests:", error);
      toast.error(error.message || "حدث خطأ أثناء جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch requests when dependencies change
  useEffect(() => {
    fetchRequests();
  }, [page, pageSize, status]); // Added status to dependencies

  // Effect to refetch when notification count changes (triggered by WebSocket middleware)
  useEffect(() => {
    // Only refetch if we're on this page and the count changed
    if (notificationCount > 0 && totalCount !== notificationCount) {
      console.log(
        "Cancellation requests notification count changed via WebSocket, refreshing data..."
      );
      fetchRequests();
    }
  }, [notificationCount]);

  const handleOpenModal = (request) => {
    // Check refund permission before opening modal
    if (!hasPermission(107)) {
      toast.error("ليس لديك صلاحية لإرجاع المبالغ");
      return;
    }

    setSelectedRequest(request);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRequest(null);
  };

  const handleRefund = async () => {
    if (!selectedRequest || processingRefund) return;

    // Double check refund permission before processing
    if (!hasPermission(107)) {
      toast.error("ليس لديك صلاحية لإرجاع المبالغ");
      return;
    }

    setProcessingRefund(true);
    try {
      const response = await api.patch(`/refunds/${selectedRequest._id}`);

      if (response.data.status === "success") {
        toast.success("تم إرجاع المبلغ بنجاح");
        handleCloseModal();
        fetchRequests(); // This will also update the notification count
      } else {
        toast.error("فشل في إرجاع المبلغ");
      }
    } catch (error) {
      console.error("Error processing refund:", error);
      toast.error(error.message || "حدث خطأ أثناء إرجاع المبلغ");
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleReschedule = async (request) => {
    // Check reschedule permission before opening modal
    if (!hasPermission(107)) {
      toast.error("ليس لديك صلاحية لإعادة الجدولة");
      return;
    }

    setSelectedRequest(request);
    setOpenSchedule(true);
  };

  // Function to handle successful operations that should refresh data
  const handleOperationSuccess = () => {
    fetchRequests();
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "الإجراءات",
      minWidth: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // Check if user has permission for refund and reschedule actions
        const canProcessActions = hasPermission(107);

        return (
          <div className="cell-action">
            <ColorButton
              text="ارجاع المبلغ"
              size="medium"
              variant="outlined"
              color="red"
              onClick={() => handleOpenModal(params.row)}
              disabled={!canProcessActions}
              style={{
                opacity: canProcessActions ? 1 : 0.5,
                cursor: canProcessActions ? "pointer" : "not-allowed",
              }}
            />
            <BlueButton
              text="اعادة جدولة"
              size="medium"
              variant="fill"
              onClick={() => handleReschedule(params.row)}
              disabled={!canProcessActions}
              style={{
                opacity: canProcessActions ? 1 : 0.5,
                cursor: canProcessActions ? "pointer" : "not-allowed",
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          طلبات الالغاء
          {notificationCount > 0 && (
            <span className="notification-badge">({notificationCount})</span>
          )}
        </p>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={CancellationColumns.concat(actionColumn)}
          rows={requests.map((request, index) => ({
            ...request,
            id: request._id || index,
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1);
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "طلب في الصفحة",
            },
          }}
        />
      </div>

      {/* Only show modal if user has permission */}
      {modalOpen && selectedRequest !== null && hasPermission(104) && (
        <Modal
          open={modalOpen}
          onClose={!processingRefund ? handleCloseModal : undefined}
          aria-labelledby="refund-confirmation-modal"
          aria-describedby="modal-to-confirm-refund-action"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              outline: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 2,
            }}
          >
            <div
              className="modal-header"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <img
                src={CloseIcon}
                alt="إغلاق"
                onClick={!processingRefund ? handleCloseModal : undefined}
                style={{ cursor: "pointer", width: "24px", height: "24px" }}
              />
            </div>

            <div
              className="modal-body"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "1rem 0",
              }}
            >
              <img
                src={RejectGif}
                alt="رفض"
                style={{ maxWidth: "150px", marginBottom: "1rem" }}
              />
              <h4>هل أنت متأكد من إرجاع المبلغ؟</h4>
            </div>

            <div
              className="modal-footer"
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <BlueButton
                text={processingRefund ? "جاري التنفيذ..." : "تأكيد"}
                onClick={handleRefund}
                size="medium"
                variant="fill"
                disabled={processingRefund}
              />
              <ColorButton
                text="إلغاء"
                onClick={handleCloseModal}
                size="medium"
                variant="outlined"
                color="gray"
                disabled={processingRefund}
              />
            </div>
          </Box>
        </Modal>
      )}

      {/* Only show reschedule modal if user has permission */}
      {openSchedule && hasPermission(104) && (
        <SubscriptionBookingModal
          open={openSchedule}
          onClose={() => setOpenSchedule(false)}
          onBookingComplete={handleOperationSuccess}
          bookingId={selectedRequest?.booking?._id}
          categoryId={selectedRequest?.booking?.technician?.category}
        />
      )}
    </div>
  );
};

export default CancellationRequests;
